/* eslint-disable @typescript-eslint/camelcase, @typescript-eslint/no-explicit-any, @typescript-eslint/ban-ts-ignore  */
import React, { useState, useEffect } from 'react';
import { AxiosResponse } from 'axios';
import * as Survey from 'survey-react';
import queryString from 'query-string';
import { toast } from 'react-toastify';
import { Grid, Paper, makeStyles, createStyles } from '@material-ui/core';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';
import { SurveyInstance, utils } from '@deeptrue-frontend/ui';
import { apiCall, assistantApi } from 'api/apiCall';
import Loader from 'components/basics/loader/loader';
import { API_STATUS, API_ASSISTANT_STATUS } from 'api/status';
import { SURVEY_STATUS } from 'consts/survey_status';
import { generateTemplates } from './generateTemplates';
import { Detect_IE } from '../../utils/detectIE';
import { getTranslationForLanguageSelection } from './i18n/locales';
import LocaleWidget from './LanguageSelector/Base';

utils.survey.generateTranslationLocales(Survey);
utils.survey.adoptGreekLocale(Survey);
utils.survey.adoptUkrainianLocale(Survey);

// Check if rendered on Internet explorer
const isIE = Detect_IE();

const useStyles = makeStyles(() =>
  createStyles({
    paper: {
      overflow: isIE ? 'visible' : 'hidden',
      boxShadow: 'none',
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      padding: '2rem 1rem',
    },
  })
);

const SurveyContainer = () => {
  const [survey, setSurvey] = useState<{
    isLoadingSurvey: boolean;
    locale?: string;
    json: any;
    survey_context: any;
    isOnePagedSurvey: boolean;
    supportedLocales: string[];
  }>({
    isLoadingSurvey: true,
    json: {},
    survey_context: {
      currentPageValue: 0,
    },
    isOnePagedSurvey: false,
    supportedLocales: ['en'],
    locale: undefined,
  });
  const history = useHistory();
  const { uuid } = useParams();
  const { search, pathname } = useLocation();
  const classes = useStyles();

  const urlParameters = queryString.parse(search);
  const isTest = pathname.indexOf('/test/') !== -1;

  const onComplete = (surveyData: any) => {
    const { data, locale } = surveyData;
    const isStarted = uuid && Cookies.get(`__survey_${uuid}`) === 'true';

    const resultsToSend = data.dtstartinglocale
      ? data
      : { ...data, dtstartinglocale: locale };

    const body = {
      result: resultsToSend,
      completed: true,
      key_uuid: uuid,
    };
    const { isOnePagedSurvey } = survey;
    const surveyUrl = `keys/results/${uuid}/`;
    const testSurveyUrl = `/keys/test/${uuid}/complete/`;
    const isFirstReqest = isTest || isOnePagedSurvey || !isStarted;
    const requestMethod = isFirstReqest ? 'POST' : 'PATCH';

    const apiUrl = isTest ? testSurveyUrl : surveyUrl;
    return apiCall(apiUrl, requestMethod, body).then(({ data }: any) => {
      const { completed, redirect_url } = data;
      if (completed && redirect_url) {
        return window.location.replace(redirect_url);
      }
    });
  };

  const onCurrentPageChanged = (surveyInstance: any) => {
    const isStarted = uuid && Cookies.get(`__survey_${uuid}`) === 'true';
    if (isTest) return;
    const surveyApiMethod = isStarted ? 'PATCH' : 'POST';
    const { data, locale, currentPageNo } = surveyInstance;

    const resultsToSend = data.dtstartinglocale
      ? data
      : { ...data, dtstartinglocale: locale };

    const body = {
      result: {
        ...resultsToSend,
        currentPageValue: currentPageNo,
      },
      completed: data.status === 'screenout' ? true : false,
      key_uuid: uuid,
    };

    const preparedUrl = isStarted ? `keys/results/${uuid}/` : `keys/results/`;

    if (data.status === 'screenout') {
      return surveyInstance.doComplete();
    }

    return apiCall(preparedUrl, surveyApiMethod, body).then(
      (response: AxiosResponse) => {
        if (uuid) {
          Cookies.set(`__survey_${uuid}`, 'true');
        }
        const { new_values, completed, redirect_url } = response.data;

        if (completed && redirect_url) {
          return window.location.replace(redirect_url);
        }

        if (typeof new_values !== 'undefined') {
          if (new_values.status === SURVEY_STATUS.QUOTAFULL)
            return surveyInstance.completeLastPage();

          Object.entries(new_values).forEach(([key, value]) => {
            surveyInstance.setValue(key, value);
            surveyInstance.render();
          });
        }
      }
    );
  };

  const getAvailableLanguages = (json: { [key: string]: any }) => {
    // @ts-ignore
    const locales = new Survey.Model(json).getUsedLocales();

    return locales;
  };

  const selectLanguages = (languages?: { [key: string]: string[] }) => {
    const { country } = queryString.parse(search);

    if (languages !== undefined && country !== undefined) {
      return languages[country as string];
    }

    return undefined;
  };

  const getSurvey = (
    surveyUrl: string,
    languages?: { [key: string]: string[] }
  ) => {
    return apiCall(surveyUrl)
      .then(async ({ data }: any) => {
        const { json, survey_context, is_started } = data;
        const {
          original_briefing: { Blanguages },
        } = survey_context;

        const pages = generateTemplates(survey_context, json.pages);

        json.pages = pages;

        // Check available languages
        const localeToDisplay: string[] = [];
        const localesAvailableInSurvey = getAvailableLanguages(json);
        const countryLocales = selectLanguages(languages);

        if (languages && countryLocales) {
          const commonLocale: string[] = [];

          countryLocales.forEach((countryCode: string) => {
            if (localesAvailableInSurvey.includes(countryCode)) {
              commonLocale.push(countryCode);
            }
          });

          if (commonLocale.length > 0) {
            localeToDisplay.push(...commonLocale);
          } else {
            localeToDisplay.push(...localesAvailableInSurvey);
          }
        } else {
          localeToDisplay.push(...localesAvailableInSurvey);
        }

        const filterLocaleByBriefingSelection = () => {
          const commonLocale: string[] = [];

          if (Blanguages) {
            localeToDisplay.forEach((countryCode: string) => {
              if (Blanguages.includes(countryCode)) {
                commonLocale.push(countryCode);
              }
            });
          } else {
            commonLocale.push(...localeToDisplay);
          }

          return commonLocale;
        };

        const localeFilteredBySelectedLanguagesInBriefing = isTest
          ? localeToDisplay
          : filterLocaleByBriefingSelection();

        const translations = getTranslationForLanguageSelection(
          isTest
            ? ['en', 'de', 'fr', 'it']
            : localeFilteredBySelectedLanguagesInBriefing
        );

        const localesInUserLanguage = (locale: string) => {
          const isLanguageTranslated = utils.i18n.isLanguageTranslated(locale);

          if (urlParameters.user_lang && isLanguageTranslated) {
            const translatedLocaleByUserLang = utils.i18n.translateLanguage(
              locale,
              urlParameters.user_lang
                ? urlParameters.user_lang.toString()
                : 'en'
            );

            return translatedLocaleByUserLang;
          }
          // Left on purpose
          console.log(`Language '${locale}' is not translated`);
          return Survey.surveyLocalization.localeNames[locale.toLowerCase()];
        };

        // IF there will be more than one language to choose we add question to choose
        if (localeFilteredBySelectedLanguagesInBriefing.length > 1) {
          const localePage = {
            name: 'languages',
            elements: [
              {
                name: 'dtstartinglocale',
                type: isTest ? 'dtlocale' : 'radiogroup',
                title: {
                  default: translations.title.join(' '),
                },

                baseLocales:
                  Blanguages &&
                  Blanguages.map((locale: string) => ({
                    value: locale,
                    text: {
                      default:
                        Survey.surveyLocalization.localeNames[
                          locale.toLowerCase()
                        ],
                    },
                  })),
                choices: localeFilteredBySelectedLanguagesInBriefing.map(
                  (locale: string) => ({
                    value: locale,
                    text: {
                      default:
                        Survey.surveyLocalization.localeNames[
                          locale.toLowerCase()
                        ],
                    },
                  })
                ),
                avialableLocales:
                  localeFilteredBySelectedLanguagesInBriefing.map(
                    (locale: string) => ({
                      value: locale,
                      text: {
                        default: localesInUserLanguage(locale),
                      },
                    })
                  ),
                isLocale: true,
                isRequired: true,
                description: {
                  default: translations.description.join(' '),
                },
                userLang: urlParameters.user_lang,
                hasTooltip: isTest,
                tooltip:
                  "This is a preview. In the live questionnaire, only the languages spoken in the target country and selected by you are displayed. <br/> <br/> Dies ist eine Vorschau. Im Live-Fragebogen werden nur die im Zielland gesprochenen und von dir ausgewählten Sprachen angezeigt. <br/> <br/> Ceci est un aperçu. Dans le questionnaire en direct, seules les langues parlées dans le pays cible et sélectionnées par toi sont affichées. <br/> <br/> Questa è un'anteprima. Nel questionario dal vivo, vengono visualizzate solo le lingue parlate nel paese di destinazione e selezionate da te.",
              },
            ],
          };
          json.pages = [localePage, ...json.pages];
        }

        let surveyContextWithArgs = {
          ...survey_context,
          ...survey_context.original_briefing,
          ...urlParameters,
        };

        surveyContextWithArgs.age = parseInt(surveyContextWithArgs.age);

        const isOnePagedSurvey = json.pages.length === 1;

        if (is_started) {
          await apiCall(`keys/results/${uuid}/`).then(
            ({ data }: { data: any }) => {
              surveyContextWithArgs = {
                ...surveyContextWithArgs,
                ...data.result,
              };
            }
          );
        }
        if (uuid) {
          Cookies.set(`__survey_${uuid}`, is_started);
        }

        // function to filter out question about age and gender, if survey is provided by cint
        const getFilteredJson = () => {
          if (
            (!is_started &&
              survey_context.ProviderSlug === 'cint' &&
              urlParameters.age &&
              urlParameters.gender) ||
            (is_started && json.isAgeAndGenderImported)
          ) {
            const filteredPages = json.pages.filter(
              (page: any) => page.name !== 'age_gender'
            );
            return {
              ...json,
              pages: filteredPages,
              isAgeAndGenderImported: true,
            };
          }

          return json;
        };

        setSurvey({
          json: {
            locale:
              localeFilteredBySelectedLanguagesInBriefing.length === 1
                ? localeFilteredBySelectedLanguagesInBriefing[0].toLowerCase()
                : null,
            ...getFilteredJson(),
          },
          survey_context: surveyContextWithArgs,
          isOnePagedSurvey: isOnePagedSurvey,
          locale:
            localeFilteredBySelectedLanguagesInBriefing.length === 1
              ? localeFilteredBySelectedLanguagesInBriefing[0].toLowerCase()
              : undefined,
          supportedLocales: localeFilteredBySelectedLanguagesInBriefing,
          isLoadingSurvey: false,
        });
      })
      .catch((err: any) => {
        if (err && err.response) {
          const { status } = err.response;

          toast.error(API_STATUS[status || 500]);
        }

        setSurvey({
          json: {},
          survey_context: {
            currentPageValue: 0,
          },
          isLoadingSurvey: false,
          isOnePagedSurvey: false,
          supportedLocales: ['en'],
          locale: undefined,
        });

        if (err && err.response.status === 404) {
          if (err.response.data.status === 'finished') {
            history.push('/finished');
          }
        } else {
          history.push('/');
        }
      });
  };

  useEffect(() => {
    const { country } = queryString.parse(search);
    // XX stands for Global survey all languages posible
    const globalCountry = 'XX';

    if (isTest) {
      if (country && country !== globalCountry) {
        assistantApi
          .getLanguages({ id: country })
          .then(({ data }) => {
            getSurvey(`/keys/test/${uuid}/survey/`, data);
          })
          .catch(({ response }) => {
            toast.error(API_ASSISTANT_STATUS[response.status]);
            getSurvey(`keys/test/${uuid}/survey/`);
          });
      } else {
        getSurvey(`/keys/test/${uuid}/survey/`);
      }
    } else {
      if (country && country !== globalCountry) {
        assistantApi
          .getLanguages({ id: country })
          .then(({ data }) => {
            getSurvey(`keys/${uuid}/survey/`, data);
          })
          .catch(({ response }) => {
            toast.error(API_ASSISTANT_STATUS[response.status]);
            getSurvey(`keys/${uuid}/survey/`);
          });
      } else {
        getSurvey(`keys/${uuid}/survey/`);
      }
    }

    return () => {
      if (uuid) {
        Cookies.remove(`__survey_${uuid}`);
      }
    };
    // eslint-disable-next-line
  }, []);

  return survey.isLoadingSurvey ? (
    <Grid container justify="center" alignItems="center">
      <Grid xs={12} sm={10} md={7} lg={5} item>
        <Paper className={classes.paper}>
          <Loader info="Downloading resources..." />
        </Paper>
      </Grid>
    </Grid>
  ) : (
    <Grid container justify="center" alignItems="center">
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <SurveyInstance
            mode="edit"
            json={survey.json}
            initialValues={survey.survey_context}
            onComplete={onComplete}
            onCurrentPageChanged={onCurrentPageChanged}
            currentPage={survey.survey_context.currentPageValue}
            locale={survey.survey_context.dtstartinglocale || survey.locale}
            isLangSwitcher={isTest}
            externalWidgets={[LocaleWidget]}
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default SurveyContainer;
